/**
 * `operatorConsole` — the Platform Operator console's vocabulary, its
 * pure helpers and its request-ordering machinery.
 *
 * Task 307A. Task file: `tasks/307A-platform-operator-console.md`.
 * The machinery below is Task 306A's, MOVED here verbatim (§3 of the
 * 307A task file) so the agency LIST and the agency DETAIL — which are
 * two routes now, not one page with a disclosure — import ONE copy of it
 * rather than each growing its own.
 *
 * ---------------------------------------------------------------------
 * WHY THIS FILE HAS NO JSX AND IMPORTS NO REACT.
 *
 * This repository has no DOM test environment and no `@testing-library`,
 * so a page component cannot be driven by a test: an effects-gated page
 * can only be PINNED as a source string. A pin proves an expression is
 * what it is and can never prove it is right — the lesson 306A §9.3
 * records twice and 301B's QA found the hard way.
 *
 * So everything that has a rule in it lives here, as an exported
 * function with its dependencies passed in, and the tests EXECUTE it.
 * The two page modules keep only wiring and markup. That split is the
 * reason the out-of-order settlements, the turnstile, the freshness
 * boundary and the query round-trip are all tested rather than asserted
 * by comment.
 *
 * NOTHING HERE TALKS TO THE NETWORK EITHER. Every loader takes its fetch
 * and its writes as a runtime record, so a test supplies both and no
 * `vi.mock` of the API client is needed.
 * ---------------------------------------------------------------------
 */

import type { ListOrganizationCommercialStateParams } from '../../api/endpoints';
import { OPTIONAL_AGENCY_PROFILE_FIELD_KEYS } from '../../api/types';
import { COMMERCIAL_LIST_SORTS, COMMERCIAL_STATES } from '../../api/types';
import type {
  AgencyProfileFieldKey,
  CommercialAction,
  CommercialListPlan,
  CommercialListSort,
  CommercialState,
  ListOrganizationCommercialStateData,
  OrganizationCommercialDetailData,
  OrganizationCommercialStateRow,
} from '../../api/types';

/**
 * Every operation the console's action machinery guards.
 *
 * The six commercial transitions plus manual plan assignment. It is ONE
 * union rather than two parallel run-functions because everything
 * {@link runCommercialAction} enforces — a non-blank reason, the
 * per-organization turnstile, the busy claim held to a freshness
 * boundary, the separation of a failed operation from a failed refresh —
 * is required identically by both, and duplicating it to add a seventh
 * operation is how the two copies drift.
 *
 * Only {@link isDestructiveAction} distinguishes them, and a plan
 * assignment is not destructive: it changes what the tenant may use, it
 * does not cut anyone off.
 */
export type OperatorAction = CommercialAction | 'assign_plan';

/**
 * The confirmation copy 295A §5.4 mandates. Suspension and termination
 * reach the agency's client-portal users too, and the operator must be
 * told that BEFORE acting, not after.
 */
export const DESTRUCTIVE_ACTION_WARNING =
  'تنبيه: إيقاف أو إنهاء الوكالة يوقف أيضاً وصول مستخدمي عملائها إلى بوابة العميل، ' +
  'لأن بوابة العميل جزء من نطاق الوكالة. تأكد قبل المتابعة.';

export const ACTION_LABELS: Readonly<Record<CommercialAction, string>> = {
  activate: 'تفعيل',
  suspend: 'إيقاف مؤقت',
  reinstate: 'إعادة تفعيل',
  terminate: 'إنهاء',
  mark_paid: 'تعليم كمدفوع',
  mark_unpaid: 'تعليم كغير مدفوع',
};

/**
 * Ledger action labels. A SUPERSET of {@link ACTION_LABELS}: the ledger
 * also carries `migration_backfill`, which is not an operator action and
 * can never be offered, but IS a row the operator will read on any tenant
 * that predates Task 298's backfill.
 */
export const LEDGER_ACTION_LABELS_AR: Readonly<Record<string, string>> = {
  ...ACTION_LABELS,
  migration_backfill: 'ترحيل أولي (نظام)',
};

export const COMMERCIAL_STATE_LABELS_AR: Readonly<Record<CommercialState, string>> = {
  pending_activation: 'بانتظار التفعيل',
  activated: 'مفعّلة',
  suspended: 'موقوفة',
  terminated: 'منتهية',
};

export const PAYMENT_STATUS_LABELS_AR: Readonly<Record<string, string>> = {
  unpaid: 'غير مدفوع',
  paid: 'مدفوع',
};

/**
 * The eight Task 299 profile fields, named the way the agency saw them.
 *
 * Keyed off {@link AGENCY_PROFILE_FIELD_KEYS} so a field added to the
 * boundary and not to this map is a TypeScript error rather than a blank
 * row on the one screen where "is this complete" is the question.
 */
export const PROFILE_FIELD_LABELS_AR: Readonly<Record<AgencyProfileFieldKey, string>> = {
  legal_name: 'الاسم النظامي',
  country_code: 'الدولة',
  city: 'المدينة',
  phone_e164: 'رقم الهاتف',
  commercial_registration_no: 'السجل التجاري',
  vat_no: 'الرقم الضريبي',
  billing_contact_name: 'مسؤول الفوترة',
  billing_contact_email: 'بريد مسؤول الفوترة',
};

/** The marker the agency's OWN onboarding form already puts on these two. */
export const OPTIONAL_FIELD_MARKER_AR = '(اختياري)';

/**
 * The label the operator reads, marked optional for exactly the fields
 * Task 299 froze as optional.
 *
 * Derived from `OPTIONAL_AGENCY_PROFILE_FIELD_KEYS` rather than baked
 * into {@link PROFILE_FIELD_LABELS_AR}, so the marker, the completion
 * denominator and the server's `completed_at` trigger all move together
 * or not at all. The agency sees «رقم السجل التجاري (اختياري)» on the form
 * it filled in; an operator screen that then lists the same blank field
 * as «ناقص» is telling two people opposite things about one requirement,
 * and it is the operator's version that decides an activation.
 */
export function profileFieldLabel(key: AgencyProfileFieldKey): string {
  return isOptionalProfileField(key)
    ? `${PROFILE_FIELD_LABELS_AR[key]} ${OPTIONAL_FIELD_MARKER_AR}`
    : PROFILE_FIELD_LABELS_AR[key];
}

export function isOptionalProfileField(key: AgencyProfileFieldKey): boolean {
  return (OPTIONAL_AGENCY_PROFILE_FIELD_KEYS as ReadonlyArray<string>).includes(key);
}

/**
 * Which actions are OFFERED for a given standing. This mirrors the
 * server's state machine (295A §5.4) so the operator is not invited to
 * make a call that will 409 — but the server is the authority, and
 * `terminated` offers nothing because it is ABSORBING (invariant A3).
 */
export function actionsForState(state: CommercialState): ReadonlyArray<CommercialAction> {
  switch (state) {
    case 'pending_activation':
      return ['activate', 'terminate', 'mark_paid', 'mark_unpaid'];
    case 'activated':
      return ['suspend', 'terminate', 'mark_paid', 'mark_unpaid'];
    case 'suspended':
      return ['reinstate', 'terminate', 'mark_paid', 'mark_unpaid'];
    case 'terminated':
      return [];
    default:
      return [];
  }
}

/**
 * Task 307A: a plan assignment is NOT destructive. It changes what the
 * tenant may use; it cuts nobody off, and demanding the client-portal
 * blast-radius confirmation for it would train operators to click
 * through the one warning that matters.
 */
export function isDestructiveAction(action: OperatorAction): boolean {
  return action === 'suspend' || action === 'terminate';
}

/**
 * A timestamp the operator can act on.
 *
 * IN THE READER'S OWN TIMEZONE, and rendered `dir="ltr"` by the caller: an
 * ISO string dropped into an RTL paragraph has its date and time
 * components reordered by the bidi algorithm, which on a decision log is
 * not a cosmetic problem. Latin digits and the Gregorian calendar, because
 * every other timestamp in this product's operator surfaces uses them and
 * an operator comparing two screens must not have to convert.
 */
export function formatOperatorTimestamp(value: string | null | undefined): string {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  try {
    return new Intl.DateTimeFormat('en-GB', {
      dateStyle: 'medium',
      timeStyle: 'short',
      calendar: 'gregory',
      numberingSystem: 'latn',
    }).format(parsed);
  } catch {
    return parsed.toISOString();
  }
}

/**
 * A nullable text value, as the operator should read it.
 *
 * A blank string renders as «—», the same as null: `upsert-agency-profile`
 * stores `null` for a cleared field, but whitespace is still not a legal
 * name, and the completion summary counts it as missing. The two must
 * agree.
 */
export function displayValue(value: string | null | undefined): string {
  return typeof value === 'string' && value.trim().length > 0 ? value : '—';
}

/**
 * "Is this settled request still the one being waited for?" — per key.
 *
 * Two reads for the SAME organization can be in flight at once: collapse
 * and re-expand issues one, and a completed transition re-reads while the
 * first is still open. Without this the older response can settle LAST and
 * put the pre-transition standing, ledger and action buttons back on
 * screen — a stale answer that looks exactly like a fresh one.
 *
 * A plain counter rather than an `AbortController`: aborting would cancel
 * a read the operator is actively waiting for, and the goal is to IGNORE a
 * superseded answer, not to prevent it arriving.
 *
 * Exported and dependency-free so the out-of-order case is executed by
 * test rather than asserted by comment.
 * ---------------------------------------------------------------------
 * IT ALSO ANSWERS A SECOND QUESTION — "has a snapshot at least this fresh
 * reached the page yet?" — Task 306A §1.5.2.
 *
 * A completed transition must not hand the operator their controls back
 * while the row they just changed still shows the pre-transition state.
 * That needs a boundary, and the boundary cannot be "my own request
 * resolved": a request that was SUPERSEDED resolved having written
 * nothing. Nor can it be "wait for whatever superseded me", because the
 * superseding request may itself be superseded, and an unbounded chain of
 * promises proves nothing about what is on screen.
 *
 * So the sequence keeps a second number per key — the **commit
 * watermark**: the generation of the last request that actually WROTE.
 * `settle(key, token)` raises it, and is called only from a guarded
 * settlement, so an older answer that returned early can never raise it.
 * `settled(key, token)` resolves once the watermark reaches `token` — by
 * that request's own settlement, or by any LATER one, which is by
 * construction at least as fresh. That is the proof the caller needs, and
 * it is monotonic, so it cannot be satisfied by a stale write.
 *
 * `issued(key)` is the newest token handed out so far. A caller that has
 * just started a load reads it to learn which token to wait for, which is
 * sound because every loader in this file takes its token BEFORE its
 * first `await` — a property the suite executes rather than assumes.
 *
 * The one thing this cannot survive is a request that never settles at
 * all (a hung fetch). Then the watermark never moves and the waiter never
 * resolves — the controls stay disabled. That fails CLOSED, and it is the
 * same outcome the page already had for a hung list read, which leaves it
 * on the spinner with the table unrendered.
 */
export interface LatestPerKey {
  /** Start a request for `key` and take its generation token. */
  begin(key: string): number;
  /** True only for the most recently issued token for `key`. */
  isCurrent(key: string, token: number): boolean;
  /** The newest token handed out for `key`; 0 before the first. */
  issued(key: string): number;
  /** Record that `token`'s settlement WROTE. Raises the watermark. */
  settle(key: string, token: number): void;
  /** Resolves once a settlement at least as new as `token` has written. */
  settled(key: string, token: number): Promise<void>;
}

export function createLatestPerKey(): LatestPerKey {
  const generation = new Map<string, number>();
  const committed = new Map<string, number>();
  const waiting = new Map<
    string,
    Array<{ readonly token: number; readonly resolve: () => void }>
  >();
  return {
    begin(key) {
      const next = (generation.get(key) ?? 0) + 1;
      generation.set(key, next);
      return next;
    },
    isCurrent(key, token) {
      return generation.get(key) === token;
    },
    issued(key) {
      return generation.get(key) ?? 0;
    },
    settle(key, token) {
      if (token <= (committed.get(key) ?? 0)) return;
      committed.set(key, token);
      const queue = waiting.get(key);
      if (queue === undefined) return;
      const ready = queue.filter((w) => w.token <= token);
      const rest = queue.filter((w) => w.token > token);
      if (rest.length === 0) waiting.delete(key);
      else waiting.set(key, rest);
      for (const w of ready) w.resolve();
    },
    settled(key, token) {
      if ((committed.get(key) ?? 0) >= token) return Promise.resolve();
      return new Promise<void>((resolve) => {
        const queue = waiting.get(key) ?? [];
        queue.push({ token, resolve });
        waiting.set(key, queue);
      });
    },
  };
}

/**
 * The same question for a stream that has only ONE key: the organization
 * LIST — Task 306A §10.2.
 *
 * Changing the state filter starts a second
 * `listOrganizationCommercialState` while the first is still open, and so
 * does a completed transition, which re-reads the list. Without a token
 * the older answer can settle LAST and write `rows`/`total` gathered under
 * the PREVIOUS filter beneath the heading of the one now selected, or put
 * its `error` over a newer failure, or clear `loading` while the newer
 * request is still in flight. This is the screen on which an activation is
 * decided, so the rows under the heading have to be the rows the heading
 * names.
 *
 * It is {@link createLatestPerKey} with exactly one key rather than a
 * second counter, because two counters that must behave identically are
 * one counter — and the key is private to this closure, so it can never
 * collide with an organization id. The detail sequence keeps its own
 * instance: the list and a row are separate streams, and neither may
 * supersede the other.
 */
export interface LatestRequest {
  /** Start a request and take its generation token. */
  begin(): number;
  /** True only for the most recently issued token. */
  isCurrent(token: number): boolean;
  /** The newest token handed out so far; 0 before the first. */
  issued(): number;
  /** Record that `token`'s settlement WROTE. Raises the watermark. */
  settle(token: number): void;
  /** Resolves once a settlement at least as new as `token` has written. */
  settled(token: number): Promise<void>;
}

export function createLatestRequest(): LatestRequest {
  const perKey = createLatestPerKey();
  const ONLY_KEY = 'list';
  return {
    begin: () => perKey.begin(ONLY_KEY),
    isCurrent: (token) => perKey.isCurrent(ONLY_KEY, token),
    issued: () => perKey.issued(ONLY_KEY),
    settle: (token) => perKey.settle(ONLY_KEY, token),
    settled: (token) => perKey.settled(ONLY_KEY, token),
  };
}

/**
 * Everything the list loader needs from the page, as data.
 *
 * The loader is extracted and the seam is this record for the same reason
 * §2.6.6 extracted `runContractAction`: this repository has no DOM test
 * environment and no `@testing-library`, so `PlatformCommercialPage`
 * cannot be driven, and a loader left inline could only be PINNED as a
 * source string. A pin proves an expression is what it is; it can never
 * prove the expression is right — the §2.5 lesson §9.3 records twice. The
 * loader the tests execute is now the loader that ships.
 */
export type CommercialListRuntime = {
  readonly fetchList: (
    params: ListOrganizationCommercialStateParams,
  ) => Promise<ListOrganizationCommercialStateData>;
  readonly setRows: (rows: ReadonlyArray<OrganizationCommercialStateRow>) => void;
  readonly setTotal: (total: number) => void;
  /**
   * The two fields Task 307A added to the SAME response — the assignable
   * catalogue behind the plan filter, and the unfiltered "awaiting
   * activation" figure.
   *
   * They are written HERE, from the one read, rather than by a second
   * effect issuing the same request again. A duplicate request would
   * double the load on the operator screen and, worse, could settle out
   * of order against this one: the filter's options and the row it
   * filtered would then be describing two different answers.
   *
   * Written under the SAME guard as `setRows`, so a superseded answer
   * cannot repaint the filter either.
   */
  readonly setExtras: (extras: CommercialListExtras) => void;
  readonly setError: (error: unknown) => void;
  readonly setLoading: (loading: boolean) => void;
};

export type CommercialListExtras = {
  readonly plans: ReadonlyArray<CommercialListPlan>;
  /** Null when the page came back empty; the console then hides it. */
  readonly pendingTotal: number | null;
};

/**
 * A settlement's right to write, decided ONCE and TOTALLY - §1.5.
 *
 * `superseded` and `write` are the two ordinary answers. `undecided` is
 * the third, and it exists because {@link LatestRequest} and
 * {@link LatestPerKey} are exported INTERFACES: the loaders below are
 * handed an implementation, and an implementation whose `isCurrent`
 * throws would otherwise take the loader's `catch`/`finally` with it,
 * rejecting a `void`-ed call and leaving the spinner up for ever. Round
 * 22 named that (§9.3).
 *
 * `undecided` is resolved fail-closed on DATA and fail-open on the
 * SPINNER: the settlement does not write rows it can no longer prove are
 * current, but it does clear `loading` and put the page into its
 * retryable error state, so the operator is never stranded on a spinner
 * with no way back.
 */
type SettlementVerdict = 'write' | 'superseded' | 'undecided';

/**
 * The token a call holds when `begin()` itself threw.
 *
 * Not a real generation: `begin` returns 1 upwards, so no request can
 * ever hold this. A call that never entered the sequence is the ONLY one
 * that can report its own failure - nothing newer is holding its place -
 * so it is allowed to write and is never asked to raise the watermark.
 */
export const NO_TOKEN = -1;

/**
 * What a load hands back SYNCHRONOUSLY - Task 306A §1.5.2.
 *
 * Round 23 is why this exists. The page used to issue a load and then ask
 * the sequence `issued()` which token to wait on, which is wrong in
 * exactly the case the token protocol exists for: if `begin()` THREW,
 * `issued()` names the PREVIOUS request - one that may have been issued
 * before the transition - and waiting on it would release the operator's
 * controls on a pre-transition snapshot. A caller must never have to infer
 * which token it holds; it is handed one.
 *
 * `settled` is this call's own completion. `fresh` is the freshness
 * boundary an action claim waits on, and the two are deliberately
 * different promises: a SUPERSEDED load settles having written nothing, so
 * its own completion proves nothing about what is on screen.
 *
 * With {@link NO_TOKEN} - the sequence refused to issue one - `fresh` IS
 * `settled`. There is nothing newer holding this call's place, and by the
 * time it settles the failure has been reported and `loading` cleared, so
 * the page is on its error state with the table unrendered and no stale
 * control on screen. Adopting an older request's token instead would be
 * the round-23 defect.
 */
export type CommercialLoadHandle = {
  /** The generation this load holds, or {@link NO_TOKEN}. */
  readonly token: number;
  /** Resolves when THIS call has finished. Never rejects. */
  readonly settled: Promise<void>;
  /** Resolves once a snapshot at least as fresh as this load has committed. */
  readonly fresh: Promise<void>;
};

/** Take a token, totally: a `begin()` that throws yields {@link NO_TOKEN}. */
function claimToken(begin: () => number): {
  readonly token: number;
  readonly failure: { readonly error: unknown } | null;
} {
  try {
    return { token: begin(), failure: null };
  } catch (error: unknown) {
    return { token: NO_TOKEN, failure: { error } };
  }
}

/**
 * The freshness promise for `token`, or `settled` if the sequence cannot
 * give one. A `settled()` that throws must not reject the caller.
 */
function freshAfter(settled: Promise<void>, wait: () => Promise<void>): Promise<void> {
  try {
    return wait();
  } catch {
    return settled;
  }
}

function writeVerdict(isCurrent: (token: number) => boolean, token: number): SettlementVerdict {
  if (token === NO_TOKEN) return 'write';
  try {
    return isCurrent(token) ? 'write' : 'superseded';
  } catch {
    return 'undecided';
  }
}

/**
 * Raise the watermark without letting a broken sequence reject a caller.
 *
 * A `settle` that throws leaves the waiter unresolved, so the action
 * claim that is waiting on it is never handed back and the controls stay
 * disabled. That fails CLOSED, which is the right direction on this
 * screen, and it cannot become an unhandled rejection.
 */
function settleQuietly(settle: () => void): void {
  try {
    settle();
  } catch {
    // Deliberately swallowed: see above. The caller's own protocol has
    // already reported whatever the settlement had to report.
  }
}

function clearCommercialListSnapshot(runtime: CommercialListRuntime): void {
  runtime.setRows([]);
  runtime.setTotal(0);
  runtime.setExtras({ plans: [], pendingTotal: null });
}

/**
 * The error a settlement reports when the sequence could not be consulted.
 *
 * `ErrorState` renders only typed wire codes as text, so this string is
 * never shown to an operator; what it does is put the page into the
 * error branch, which carries the retry control.
 */
export const SEQUENCE_UNAVAILABLE_ERROR = 'commercial_request_sequence_unavailable';

/**
 * Load the organization list, and let ONLY the newest request write.
 *
 * THE QUERY IS READ, NOT PASSED. It arrives as `readQuery` because the
 * token makes the wrong filter WORSE than it was without one, and the
 * caller that gets it wrong is not the one you would guess. `runAction` is
 * rebuilt on every render, and its promise chain keeps the closure it was
 * called with - so a re-read it issues after the transition settles would
 * carry the filter of the render the operator pressed the button in. That
 * re-read is issued LAST, so it takes the NEWEST token, so it is the one
 * request permitted to write, and the read for the filter now selected -
 * issued earlier, still open - is refused when it lands. The operator is
 * left on the previous filter's organizations under the heading they just
 * switched to, with nothing in flight to correct it. Without the token,
 * that same interleaving corrected itself.
 *
 * `readQuery` is called synchronously, in the same tick as `begin`, so
 * the query and the token are taken together and the request that holds
 * the newest token is by construction the request for the query that was
 * live when it was taken. A getter and not a ref parameter so the property
 * is executable: a test can change what it returns between issuing the
 * loader and calling it, which is exactly what a stale callback does.
 *
 * TASK 307A WIDENED IT FROM A FILTER TO A QUERY, and that made the
 * property strictly more load-bearing rather than merely bigger. The
 * query now carries the search term, the plan filter, the sort AND the
 * page, so an older answer permitted to write would put another page's
 * rows under this page's number — the same defect §1.5 found for the
 * state filter, with four more ways to reach it.
 *
 * THE TOKEN IS TAKEN BEFORE THE FIRST `await`, and the suite executes
 * that: `sequence.issued()` has advanced by the time this function
 * returns its promise. The page relies on it to learn which token to wait
 * for (§1.5.2), so it is a property and not an accident.
 *
 * Taking the token before the request also makes the two writes that OPEN
 * a load - `loading` true and the previous `error` cleared - the newest
 * request's own by construction, so neither needs a guard.
 *
 * Clearing the error at the open is load-bearing rather than tidy: the
 * success path writes `rows` and `total` and never touches `error`, and
 * the page renders its table only when `!loading && !error`. A retry
 * started from `ErrorState`'s `onRetry` that then SUCCEEDED would
 * otherwise keep its own rows off screen behind the failure it just
 * cleared.
 *
 * Every write after the `await` is guarded, on all three paths, and each
 * path writes different fields:
 *
 * - success   - `rows` and `total`;
 * - failure   - `error`;
 * - `finally` - `loading`, and the watermark.
 *
 * The third guard is not redundant with the other two. `finally` runs even
 * for a superseded settlement that returned early, so without it an older
 * answer would still clear `loading` and the page would drop its spinner -
 * and render an empty table with the previous total - while the request
 * whose rows it is waiting for is still open.
 *
 * The `finally` also raises the sequence's commit watermark, and only on
 * the paths that wrote. That is what lets a completed transition wait for
 * a list at least as fresh as itself before giving the operator their
 * controls back (§1.5.2); an older settlement that returned early must
 * not be able to satisfy that wait, which is why the call sits under the
 * same guard as `setLoading`.
 *
 * A counter and not an `AbortSignal`: the goal is to IGNORE a superseded
 * answer, not to cancel a read someone is waiting for.
 *
 * IT DOES NOT REJECT AND IT DOES NOT WEDGE, under ONE precondition:
 * {@link CommercialListRuntime}'s writes do not throw. React's setters
 * satisfy it. Everything else is handled rather than assumed -
 * `sequence.begin()` throwing (the call proceeds with {@link NO_TOKEN},
 * reports the failure and clears `loading`), `readQuery` or `fetchList`
 * throwing (reported through `setError`), `sequence.isCurrent` throwing
 * (`undecided`), and `sequence.settle` throwing ({@link settleQuietly}).
 * The residual is a write that throws from inside the `catch` or the
 * `finally` itself, which is the stated precondition and nothing else.
 */
export function runCommercialListLoad(
  sequence: LatestRequest,
  readQuery: () => ListOrganizationCommercialStateParams,
  runtime: CommercialListRuntime,
): CommercialLoadHandle {
  const claimed = claimToken(() => sequence.begin());
  const settled = settleCommercialListLoad(
    sequence,
    claimed.token,
    claimed.failure,
    readQuery,
    runtime,
  );
  return {
    token: claimed.token,
    settled,
    fresh:
      claimed.token === NO_TOKEN
        ? settled
        : freshAfter(settled, () => sequence.settled(claimed.token)),
  };
}

async function settleCommercialListLoad(
  sequence: LatestRequest,
  token: number,
  tokenFailure: { readonly error: unknown } | null,
  readQuery: () => ListOrganizationCommercialStateParams,
  runtime: CommercialListRuntime,
): Promise<void> {
  let verdict: SettlementVerdict = 'write';
  try {
    // Reported exactly like any other failure: through `setError`, with
    // `loading` cleared in the `finally`. A boxed failure and not a
    // nullable error, so a sequence that throws `null` is still a failure.
    if (tokenFailure !== null) throw tokenFailure.error;
    const params = readQuery();
    runtime.setLoading(true);
    runtime.setError(null);
    const data = await runtime.fetchList(params);
    verdict = writeVerdict((t) => sequence.isCurrent(t), token);
    if (verdict === 'superseded') return;
    if (verdict === 'undecided') {
      clearCommercialListSnapshot(runtime);
      runtime.setError(new Error(SEQUENCE_UNAVAILABLE_ERROR));
      return;
    }
    runtime.setRows(data.organizations);
    runtime.setTotal(data.total);
    runtime.setExtras({
      plans: data.plans ?? [],
      pendingTotal: typeof data.pending_total === 'number' ? data.pending_total : null,
    });
  } catch (err: unknown) {
    verdict = writeVerdict((t) => sequence.isCurrent(t), token);
    if (verdict === 'superseded') return;
    clearCommercialListSnapshot(runtime);
    runtime.setError(err);
  } finally {
    if (verdict !== 'superseded') {
      runtime.setLoading(false);
      if (token !== NO_TOKEN) settleQuietly(() => sequence.settle(token));
    }
  }
}

export type DetailState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly data: OrganizationCommercialDetailData }
  | { readonly status: 'error'; readonly error: unknown };

/**
 * Everything one row's detail read needs from the page, as data.
 *
 * Extracted for {@link runCommercialListLoad}'s reason: the loader the
 * tests execute has to be the loader that ships. It was inline until
 * §1.5.2, and inline it could only be PINNED - three source pins stood in
 * for its two guards, and a pin cannot show that a read a transition
 * issues is one the caller can WAIT for.
 */
export type CommercialDetailRuntime = {
  readonly fetchDetail: (organizationId: string) => Promise<OrganizationCommercialDetailData>;
  readonly setDetail: (organizationId: string, state: DetailState) => void;
};

/**
 * Load one organization's detail, and let ONLY the newest read write.
 *
 * Deliberately NOT abort-on-collapse: an operator who collapses and
 * re-expands the same row within one round trip would otherwise cancel
 * the read they are waiting for.
 *
 * LAST WRITER WINS, AND "LAST" MEANS LAST ISSUED, NOT LAST TO ARRIVE.
 * Collapse then re-expand, or a transition that re-reads while a
 * disclosure load is still in flight, issues a second request for the
 * same organization; without a generation token the OLDER response could
 * land afterwards and put the pre-transition standing, ledger and action
 * buttons back on screen.
 *
 * The protocol is {@link runCommercialListLoad}'s, keyed: the token is
 * taken before the first `await` so `sequence.issued(organizationId)`
 * names this read; both settlement paths are guarded; the `finally`
 * raises the watermark only when this read actually wrote, so a completed
 * transition can wait for a panel at least as fresh as itself (§1.5.2).
 * `begin`, `isCurrent` and `settle` throwing are all handled, so a
 * `void`-ed call cannot reject.
 *
 * There is no `loading` flag here and so no third guard: the loading
 * state IS a write to the same slot, made at the open, and the panel's
 * three states are mutually exclusive.
 */
export function runCommercialDetailLoad(
  organizationId: string,
  sequence: LatestPerKey,
  runtime: CommercialDetailRuntime,
): CommercialLoadHandle {
  const claimed = claimToken(() => sequence.begin(organizationId));
  const settled = settleCommercialDetailLoad(
    organizationId,
    sequence,
    claimed.token,
    claimed.failure,
    runtime,
  );
  return {
    token: claimed.token,
    settled,
    fresh:
      claimed.token === NO_TOKEN
        ? settled
        : freshAfter(settled, () => sequence.settled(organizationId, claimed.token)),
  };
}

async function settleCommercialDetailLoad(
  organizationId: string,
  sequence: LatestPerKey,
  token: number,
  tokenFailure: { readonly error: unknown } | null,
  runtime: CommercialDetailRuntime,
): Promise<void> {
  let verdict: SettlementVerdict = 'write';
  try {
    if (tokenFailure !== null) throw tokenFailure.error;
    runtime.setDetail(organizationId, { status: 'loading' });
    const data = await runtime.fetchDetail(organizationId);
    verdict = writeVerdict((t) => sequence.isCurrent(organizationId, t), token);
    if (verdict === 'superseded') return;
    if (verdict === 'undecided') {
      runtime.setDetail(organizationId, {
        status: 'error',
        error: new Error(SEQUENCE_UNAVAILABLE_ERROR),
      });
      return;
    }
    runtime.setDetail(organizationId, { status: 'ready', data });
  } catch (error: unknown) {
    verdict = writeVerdict((t) => sequence.isCurrent(organizationId, t), token);
    if (verdict === 'superseded') return;
    runtime.setDetail(organizationId, { status: 'error', error });
  } finally {
    if (verdict !== 'superseded' && token !== NO_TOKEN) {
      settleQuietly(() => sequence.settle(organizationId, token));
    }
  }
}

/**
 * One action at a time PER ORGANIZATION, claimed SYNCHRONOUSLY — §1.5.2.
 *
 * `busyOrgs` is React state, and the `disabled` attribute derived from it
 * is a HINT rather than a guard: it is not applied until the next commit,
 * so two clicks in one tick both reach the handler and both dispatch a
 * transition. §2.6.6 learned exactly this on the contract page and built
 * `createContractActionLock` for it. This is the same primitive keyed by
 * organization, because this page deliberately allows an action on B while
 * A is running — what it must forbid is two on A.
 *
 * A `Set` behind a ref, never state: a claim has to take effect on the
 * line that writes it, and a render must not be able to lose it.
 */
export interface OrganizationActionLock {
  /** Take the claim. False if this organization already has one out. */
  claim(organizationId: string): boolean;
  /** Give it back. Idempotent. */
  release(organizationId: string): void;
}

export function createOrganizationActionLock(): OrganizationActionLock {
  const running = new Set<string>();
  return {
    claim(organizationId) {
      if (running.has(organizationId)) return false;
      running.add(organizationId);
      return true;
    },
    release(organizationId) {
      running.delete(organizationId);
    },
  };
}

/** What {@link runCommercialAction} did. Never a bare boolean (§2.6). */
export type CommercialActionOutcome =
  | { readonly status: 'refused_no_reason' }
  | { readonly status: 'refused_unconfirmed' }
  | { readonly status: 'refused_busy' }
  | { readonly status: 'applied' }
  /**
   * The transition APPLIED and the re-reads that follow it threw. Distinct
   * from `failed`, which is the transition's own failure: reporting a
   * broken refresh as a broken action is the §2.6.6g defect, and reporting
   * it as a clean `applied` would be the swallowing §2.6 forbids. The
   * refreshes this page passes cannot reach it - `runCommercialListLoad`
   * and `runCommercialDetailLoad` both report through the page instead -
   * so this is the honest contract for an injected one.
   */
  | { readonly status: 'applied_refresh_failed'; readonly error: unknown }
  | { readonly status: 'failed'; readonly error: unknown };

export type CommercialActionRuntime = {
  /** The reason as typed. Read synchronously, before anything awaits. */
  readonly readReason: (organizationId: string) => string;
  readonly confirmDestructive: () => boolean;
  readonly lock: OrganizationActionLock;
  readonly transition: (reason: string) => Promise<unknown>;
  readonly setBusy: (organizationId: string, busy: boolean) => void;
  /**
   * Clear the reason and re-read what the transition invalidated.
   *
   * AWAITED, not started and forgotten. It resolves only once the page is
   * showing a snapshot at least as fresh as the transition, which is the
   * boundary the claim is held to (§1.5.2).
   */
  readonly onApplied: (organizationId: string) => Promise<void>;
  /**
   * Report - or clear - the failure for ONE organization.
   *
   * Keyed for `setBusy`'s reason, and round 23 found the unkeyed version:
   * one page-wide slot meant that merely STARTING an action on B erased
   * A's still-unread failure banner, so two organizations were still
   * coupled after the lock and the busy set had been keyed.
   *
   * `cause` is the CAUGHT VALUE, handed on rather than described, and it
   * is present ONLY on the transition-failure call. A runner that reports
   * every operation with one sentence is right for a standing transition -
   * the state machine that refused it is on the screen the operator is
   * already reading - and wrong for an operation whose refusals are not
   * visible anywhere, such as a plan assignment. Passing the error lets
   * the CALLER classify without this runner having to know which
   * operations have classifiable failures. It is optional, so a caller
   * that has nothing to say about the cause simply ignores it.
   */
  readonly setActionError: (
    organizationId: string,
    message: string | null,
    cause?: unknown,
  ) => void;
};

/**
 * The sentence a failed operation reports when nothing better is known.
 *
 * Exported so a caller that CLASSIFIES its own failures can fall back to
 * the same words rather than inventing a second generic message.
 */
export const COMMERCIAL_ACTION_FAILURE_MESSAGE =
  'تعذر تنفيذ الإجراء. تحقق من الحالة الحالية ثم أعد المحاولة.';

/**
 * Run one commercial transition, under a claim taken before the first
 * `await` — Task 306A §1.5.2.
 *
 * The order of the four gates is the whole point. The reason and the
 * destructive confirm are decided first and synchronously; `window.confirm`
 * blocks the thread, so nothing can interleave during it. The claim is then
 * taken **before any state write and before the request**, which is what
 * makes it a guard rather than a hint.
 *
 * EVERYTHING AFTER THE CLAIM IS INSIDE THE `try`, including the two writes
 * that open the action. A write that threw between the claim and the `try`
 * would leak the claim permanently and leave that one organization dead for
 * the life of the page - no release, and every later action on it refused.
 * Nothing else in this function can leak it: the two gates ABOVE the claim
 * run before it exists, so a throw from `readReason` or `confirmDestructive`
 * rejects the returned promise having taken nothing and written nothing.
 * That is the honest contract, and both halves of it are executed.
 *
 * The transition and the re-reads sit in SEPARATE `try` blocks. §2.6.6g is
 * why: this task once shipped a helper that wrapped an action and its
 * reload in one `try` and reported a failed reload as a failed action. A
 * failure here is the transition's failure and nothing else.
 *
 * THE CLAIM IS HELD ACROSS THE RE-READS, NOT JUST THE TRANSITION. This
 * is the second half of §1.5.2 and round 22's H9 finding. `onApplied`
 * used to start two `void` reads and return, so the `finally` handed the
 * claim back - and the row and the expanded panel unbusied - while the
 * page still showed the PRE-transition standing. On an absorbing move
 * like `terminate` the operator would then act on a control the server
 * has already made illegal and read the 409 as a failure of the
 * transition that in fact succeeded.
 *
 * So `onApplied` is `await`ed, and it resolves on a FRESHNESS boundary
 * rather than on its own requests returning. "My request resolved" proves
 * nothing when the request was superseded and wrote nothing; "something
 * newer is open" proves nothing about what is on screen. The sequences
 * carry a commit watermark for exactly this, and the promise resolves
 * once the watermark has reached the read this transition issued - by
 * that read's own settlement, or by any LATER one, which is by
 * construction at least as fresh (see {@link LatestPerKey}).
 *
 * The release is therefore ordered strictly AFTER every write the
 * re-reads make. React applies state updates in the order they are
 * enqueued, so no rendered frame can show this organization unbusied
 * beside rows the transition has not reached yet.
 */
export async function runCommercialAction(
  organizationId: string,
  action: OperatorAction,
  runtime: CommercialActionRuntime,
): Promise<CommercialActionOutcome> {
  const reason = runtime.readReason(organizationId).trim();
  if (reason.length === 0) {
    runtime.setActionError(organizationId, 'السبب مطلوب لكل إجراء.');
    return { status: 'refused_no_reason' };
  }
  if (isDestructiveAction(action) && !runtime.confirmDestructive()) {
    return { status: 'refused_unconfirmed' };
  }
  if (!runtime.lock.claim(organizationId)) return { status: 'refused_busy' };
  try {
    runtime.setBusy(organizationId, true);
    runtime.setActionError(organizationId, null);
    try {
      await runtime.transition(reason);
    } catch (error: unknown) {
      // The caught value travels WITH the default sentence. A caller that
      // can say something more accurate about this failure does so from
      // the error itself; one that cannot uses the sentence unchanged.
      runtime.setActionError(organizationId, COMMERCIAL_ACTION_FAILURE_MESSAGE, error);
      return { status: 'failed', error };
    }
    try {
      await runtime.onApplied(organizationId);
    } catch (error: unknown) {
      return { status: 'applied_refresh_failed', error };
    }
    return { status: 'applied' };
  } finally {
    // Nested, so the release cannot be skipped by the write above it.
    // `setBusy` is a React setter and does not throw; if a runtime hands
    // one that does, losing the claim would leave this organization dead
    // for the life of the page, which is a far worse failure than a busy
    // pill that never clears.
    try {
      runtime.setBusy(organizationId, false);
    } finally {
      runtime.lock.release(organizationId);
    }
  }
}

/**
 * The action errors with ONE organization's message set or cleared - §1.5.2.
 *
 * `actionError` was a single page-wide slot until round 23: every accepted
 * action cleared it, so starting an action on B erased A's failure before
 * the operator had read it, and the banner could not say which
 * organization it belonged to anyway. Keyed, exported, and returning a NEW
 * record for the same reasons as {@link withBusy} - except that clearing
 * an organization which has no error returns the SAME record, so a clear
 * that changes nothing cannot cause a render.
 *
 * The value is a record and not a bare string because the failure has to
 * be attributable when the row that would name it is not on screen; see
 * {@link unattachedActionErrors}.
 */
export type CommercialActionError = {
  /**
   * Carried WITH the message, not looked up from `rows` when it is
   * rendered. Round 24 is why: the row can be gone by then — the filter
   * moved, or a list refresh dropped it — and a failure the operator
   * cannot attribute is no better than one they cannot see.
   */
  readonly organizationName: string;
  readonly message: string;
};

export function withActionError(
  errors: Readonly<Record<string, CommercialActionError>>,
  organizationId: string,
  entry: CommercialActionError | null,
): Readonly<Record<string, CommercialActionError>> {
  if (entry === null) {
    if (!(organizationId in errors)) return errors;
    const next = { ...errors };
    delete next[organizationId];
    return next;
  }
  return { ...errors, [organizationId]: entry };
}

/**
 * The organizations actually ON SCREEN - Task 306A §1.5.2, generalised by
 * 307A from the list's rows to any surface.
 *
 * Not simply "the ids we have": a page renders its content only when it
 * is neither loading nor in an error state, and during either of those NO
 * organization is on screen and every failure is unattached. Taking that
 * as an argument rather than reading it makes the coupling executable,
 * and each page derives both this and its own render condition from ONE
 * value so the two cannot drift apart.
 *
 * The detail route reaches the same case the list does, by a different
 * route: start an action, navigate to another agency while it is in
 * flight, and the failure belongs to an organization no longer rendered.
 */
export function attachedIds(
  organizationIds: ReadonlyArray<string>,
  rendered: boolean,
): ReadonlySet<string> {
  return rendered ? new Set(organizationIds) : new Set();
}

/**
 * The failures whose organization has no row to render them - §1.5.2.
 *
 * Round 24's finding, and it is a defect the round-23 remediation
 * introduced: keying the failure per organization and rendering it beside
 * that organization's action buttons is right, but it made the ONLY render
 * path `rows.map`. Start an action, switch to a filter that excludes the
 * row, let the transition reject — and the failure is stored and shown
 * nowhere. The page-wide banner it replaced at least stayed visible.
 *
 * These render above the table instead, each naming its organization and
 * each dismissible, so a failure is reported wherever its row happens to
 * be. Insertion order is preserved rather than sorted: it is the order the
 * operator produced the failures in.
 */
export function unattachedActionErrors(
  errors: Readonly<Record<string, CommercialActionError>>,
  attached: ReadonlySet<string>,
): ReadonlyArray<{ readonly organizationId: string } & CommercialActionError> {
  return Object.entries(errors)
    .filter(([organizationId]) => !attached.has(organizationId))
    .map(([organizationId, entry]) => ({ organizationId, ...entry }));
}

/**
 * `busyOrgs` with ONE organization added or removed - Task 306A §1.5.2.
 *
 * The page used to hold a single `busyOrg` slot, so an action on B and a
 * settlement on A shared it: A's completion re-enabled B's controls while
 * B's request was still open, and permitted a second transition on it.
 * A set keyed by organization is the fix, and this is the whole of it -
 * exported so that "settling one row leaves the other busy" is executed
 * rather than read off the JSX.
 *
 * It returns a NEW set. Mutating the previous one in place would be a
 * state write React cannot see, so the row would stay visually busy
 * after its action had finished.
 */
export function withBusy(
  busy: ReadonlySet<string>,
  organizationId: string,
  isBusy: boolean,
): ReadonlySet<string> {
  const next = new Set(busy);
  if (isBusy) next.add(organizationId);
  else next.delete(organizationId);
  return next;
}

// ─────────────────────────────────────────────────────────────────────
// Task 307A — the list query, and why it lives in the URL
//
// Search, filter, sort and pagination are SERVER-side: the endpoint
// returns one page and a `total`, so filtering what arrived in the
// browser would be a lie the moment the result set exceeds a page.
//
// The query is therefore a value, it round-trips through the address
// bar, and it is parsed defensively. Three consequences the operator
// actually feels:
//
//   - refresh keeps the search they typed and the page they were on;
//   - Back and Forward move between queries rather than dumping them to
//     an unfiltered first page;
//   - a link to a filtered list is a link, and a link to one agency is a
//     route (`/app/platform/commercial/:organizationId`) rather than a
//     disclosure that only exists after the operator has clicked it.
//
// PARSING IS FAIL-SAFE, NOT FAIL-CLOSED, and the direction is chosen: a
// hand-edited or stale URL falls back to the default query and shows the
// unfiltered list. Refusing it would strand an operator on an error page
// over a typo, and the endpoint validates every one of these values
// again anyway — so a bad value cannot reach the database whatever this
// function does with it.
// ─────────────────────────────────────────────────────────────────────

/** One page of the operator list. Small enough to scan, per the design. */
export const OPERATOR_LIST_PAGE_SIZE = 20;

/** Mirrors the endpoint's own cap, so the input can never build a refusal. */
export const MAX_OPERATOR_SEARCH_LENGTH = 120;

/**
 * `plans.key` is a catalogue slug, and this is the ENDPOINT's own bound on
 * it — `MAX_PLAN_KEY_LENGTH` and `PLAN_KEY_RE` in
 * `list-organization-commercial-state`, restated on this side of the wire.
 *
 * A LENGTH CLIP WAS NOT A VALIDATION. `?plan=%2Fetc%2Fpasswd` was trimmed,
 * cut to 64 characters and sent, and the endpoint answered the 400 it
 * always answers for a value that is not a catalogue key — so a
 * hand-edited URL produced an error page on the one screen whose parsing
 * is documented as fail-SAFE. A key that fails the endpoint's own syntax
 * now falls back to «كل الخطط» before any request is built.
 *
 * Restated rather than imported because `supabase/functions` is Deno
 * source outside this app's module graph; the suite pins the two against
 * each other, so a divergence FAILS rather than drifts.
 */
export const MAX_OPERATOR_PLAN_KEY_LENGTH = 64;
export const OPERATOR_PLAN_KEY_RE = /^[a-z0-9][a-z0-9_-]*$/;

/**
 * The endpoint's `MAX_OFFSET`: `p_offset` is a PostgreSQL `integer`.
 *
 * The offset is DERIVED from the page — `page * OPERATOR_LIST_PAGE_SIZE` —
 * so bounding the offset at the endpoint is not enough on its own: the URL
 * names the PAGE, and `?page=999999999` built an offset of nearly twenty
 * billion. That is a 400 today and was a 500 before finding 6 bounded it,
 * for a value the operator can reach by editing the address bar.
 */
export const MAX_OPERATOR_LIST_OFFSET = 2147483647;
export const MAX_OPERATOR_LIST_PAGE = Math.floor(
  MAX_OPERATOR_LIST_OFFSET / OPERATOR_LIST_PAGE_SIZE,
);

export type OperatorListQuery = {
  readonly state: CommercialState | '';
  readonly planKey: string;
  readonly search: string;
  readonly sort: CommercialListSort;
  /** Zero-based. */
  readonly page: number;
};

export const DEFAULT_OPERATOR_LIST_QUERY: OperatorListQuery = {
  state: '',
  planKey: '',
  search: '',
  sort: 'recent',
  page: 0,
};

const QUERY_KEYS = {
  state: 'state',
  planKey: 'plan',
  search: 'q',
  sort: 'sort',
  page: 'page',
} as const;

/**
 * Read a query out of a URL search string.
 *
 * Unknown states, sorts and plan keys fall back rather than throwing;
 * `page` is floored at 0 AND bounded so the offset it derives still fits
 * the endpoint's `integer`; `search` is trimmed and clipped to the
 * endpoint's own bound. Every field of the result is a value the endpoint
 * accepts, which is what "fail-safe" has to mean here: a query this
 * function returns can never be one the endpoint refuses, so a
 * hand-edited URL lands the operator on the unfiltered list rather than
 * on a 400 they cannot read.
 */
export function parseOperatorListQuery(search: string | URLSearchParams): OperatorListQuery {
  const params = typeof search === 'string' ? new URLSearchParams(search) : search;

  const rawState = (params.get(QUERY_KEYS.state) ?? '').trim();
  const state = (COMMERCIAL_STATES as ReadonlyArray<string>).includes(rawState)
    ? (rawState as CommercialState)
    : '';

  const rawSort = (params.get(QUERY_KEYS.sort) ?? '').trim();
  const sort = (COMMERCIAL_LIST_SORTS as ReadonlyArray<string>).includes(rawSort)
    ? (rawSort as CommercialListSort)
    : DEFAULT_OPERATOR_LIST_QUERY.sort;

  // BOUNDED AT THE PAGE, because the page is what the URL names and the
  // offset is what the endpoint bounds. Anything above the last page the
  // `integer` can address is clamped to it, not refused: the read then
  // comes back empty with an authoritative `total`, which is exactly the
  // path {@link outOfRangePageRedirect} recovers from.
  const rawPage = Number.parseInt((params.get(QUERY_KEYS.page) ?? '').trim(), 10);
  const page =
    Number.isFinite(rawPage) && rawPage > 0
      ? Math.min(Math.floor(rawPage), MAX_OPERATOR_LIST_PAGE)
      : 0;

  // The endpoint's SYNTAX, not a length clip. A truncated illegal key is
  // still an illegal key, and the request built from it is still a 400.
  const rawPlanKey = (params.get(QUERY_KEYS.planKey) ?? '').trim();
  const planKey =
    rawPlanKey.length <= MAX_OPERATOR_PLAN_KEY_LENGTH && OPERATOR_PLAN_KEY_RE.test(rawPlanKey)
      ? rawPlanKey
      : '';

  return {
    state,
    planKey,
    search: (params.get(QUERY_KEYS.search) ?? '').trim().slice(0, MAX_OPERATOR_SEARCH_LENGTH),
    sort,
    page,
  };
}

/**
 * The canonical search string for a query.
 *
 * Defaults are OMITTED, so the unfiltered first page is `''` and not
 * `?state=&plan=&q=&sort=recent&page=0`. That matters for Back/Forward:
 * two navigations that mean the same thing must produce the same entry,
 * or the history stack fills with steps that change nothing on screen.
 *
 * `parseOperatorListQuery(operatorListQueryToSearch(q))` is `q` for every
 * legal `q`, and the suite executes that round trip rather than asserting
 * it.
 */
export function operatorListQueryToSearch(query: OperatorListQuery): string {
  const params = new URLSearchParams();
  if (query.state) params.set(QUERY_KEYS.state, query.state);
  if (query.planKey) params.set(QUERY_KEYS.planKey, query.planKey);
  if (query.search) params.set(QUERY_KEYS.search, query.search);
  if (query.sort !== DEFAULT_OPERATOR_LIST_QUERY.sort) params.set(QUERY_KEYS.sort, query.sort);
  if (query.page > 0) params.set(QUERY_KEYS.page, String(query.page));
  return params.toString();
}

/** The wire parameters for one query. `offset` is derived, never stored. */
export function operatorListQueryToParams(
  query: OperatorListQuery,
): ListOrganizationCommercialStateParams {
  return {
    state: query.state === '' ? undefined : query.state,
    planKey: query.planKey === '' ? undefined : query.planKey,
    search: query.search === '' ? undefined : query.search,
    sort: query.sort,
    limit: OPERATOR_LIST_PAGE_SIZE,
    offset: Math.max(0, query.page) * OPERATOR_LIST_PAGE_SIZE,
  };
}

/** How many pages `total` rows make. Always at least 1, so "1 / 1" holds. */
export function pageCount(total: number, pageSize: number = OPERATOR_LIST_PAGE_SIZE): number {
  if (!Number.isFinite(total) || total <= 0) return 1;
  return Math.max(1, Math.ceil(total / Math.max(1, pageSize)));
}

/**
 * The page an operator can actually be on.
 *
 * Deleting or filtering rows away can leave the URL pointing past the
 * end, and an out-of-range offset returns an empty page — which reads as
 * "no agencies match" when the truth is "you are past the last one".
 */
export function clampPage(
  page: number,
  total: number,
  pageSize: number = OPERATOR_LIST_PAGE_SIZE,
): number {
  if (!Number.isFinite(page) || page < 0) return 0;
  return Math.min(Math.floor(page), pageCount(total, pageSize) - 1);
}

/**
 * The page to send the operator to when the one in the URL ran off the
 * end, or `null` when the URL is fine as it is.
 *
 * THE CASE THIS EXISTS FOR. Terminate or filter enough tenants away and
 * the offset in the address bar is past the last page: the server returns
 * an empty page for a query that still matches plenty. The endpoint now
 * recovers the authoritative `total` on exactly that path, and this is
 * what the console does with it — clamp the URL to the last real page and
 * re-read. Without it the recovered total goes nowhere and the operator
 * reads «لا توجد وكالات مطابقة» over a filter that matches hundreds, with
 * no pager on screen to walk back with.
 *
 * IT IS DELIBERATELY NARROW, because a redirect that fires on the wrong
 * frame is a loop. All five conditions must hold: the read has finished,
 * it did not fail, it returned NOTHING, the server says the query DOES
 * match rows, and the clamped page differs from the one asked for. A
 * genuinely empty result set has `total === 0`, so it clamps to page 0 and
 * this returns `null` for page 0 — the empty state stands, as it should.
 */
export function outOfRangePageRedirect(
  input: {
    readonly page: number;
    readonly total: number;
    readonly rowCount: number;
    readonly loading: boolean;
    readonly failed: boolean;
  },
  pageSize: number = OPERATOR_LIST_PAGE_SIZE,
): number | null {
  if (input.loading || input.failed) return null;
  if (input.rowCount > 0) return null;
  if (!Number.isFinite(input.total) || input.total <= 0) return null;
  const clamped = clampPage(input.page, input.total, pageSize);
  return clamped === input.page ? null : clamped;
}

/**
 * The rows the design marks with the tangerine edge.
 *
 * Exactly `pending_activation`: an agency nobody has decided about yet is
 * the only thing on this screen that is WAITING on the operator. A
 * suspended or terminated tenant is a decision already made.
 */
export function needsDecision(row: { readonly commercial_state: CommercialState }): boolean {
  return row.commercial_state === 'pending_activation';
}

/**
 * The completion meter's percentage, over the SIX required fields.
 *
 * A missing or nonsensical denominator reads as 0 rather than as
 * `Infinity` or `NaN`: under-stating completeness sends the operator to
 * look at the profile, over-stating it sends them to activate.
 */
export function completionPercent(filled: number, total: number): number {
  if (!Number.isFinite(filled) || !Number.isFinite(total) || total <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((filled / total) * 100)));
}

/** The one place the detail route is spelled. */
export function agencyDetailPath(organizationId: string): string {
  return `/app/platform/commercial/${encodeURIComponent(organizationId)}`;
}

/**
 * The list route, carrying the query the operator came from.
 *
 * The detail page's "all agencies" link uses it so returning from a
 * decision lands on the filtered page that produced it, not on an
 * unfiltered first page.
 */
export function agencyListPath(search: string): string {
  const trimmed = search.startsWith('?') ? search.slice(1) : search;
  return trimmed.length > 0 ? `/app/platform/commercial?${trimmed}` : '/app/platform/commercial';
}

export const LIST_SORT_LABELS_AR: Readonly<Record<CommercialListSort, string>> = {
  recent: 'الأحدث تسجيلاً',
  least_complete: 'الأقل اكتمالاً',
  name: 'الاسم',
};

/**
 * The SaaS subscription states, named. Raw enums never appear in ordinary
 * presentation; an unmapped value falls back to itself rather than to a
 * blank, because a state nobody named is still a state the operator has
 * to see.
 */
export const SUBSCRIPTION_STATE_LABELS_AR: Readonly<Record<string, string>> = {
  trialing: 'تجريبي',
  active: 'نشط',
  past_due: 'متأخر السداد',
  suspended: 'موقوف',
  cancelled: 'ملغى',
};

export function subscriptionStateLabel(state: string | null | undefined): string {
  if (typeof state !== 'string' || state.trim().length === 0) return '—';
  return SUBSCRIPTION_STATE_LABELS_AR[state] ?? state;
}

/**
 * A plan, named for a reader.
 *
 * Arabic name first, then the key — never the key alone in ordinary
 * presentation. `plans.name_ar` is `not null` and trim-checked, so the
 * fallback chain only runs for an organization with no subscription at
 * all.
 */
export function planLabel(
  planKey: string | null | undefined,
  planNameAr: string | null | undefined,
): string {
  const name = typeof planNameAr === 'string' ? planNameAr.trim() : '';
  if (name.length > 0) return name;
  const key = typeof planKey === 'string' ? planKey.trim() : '';
  return key.length > 0 ? key : '—';
}

/**
 * Why an operator cannot submit this plan assignment yet.
 *
 * `null` means "submittable". The SAME-PLAN case is deliberately NOT
 * listed: the server refuses it (409 `same_plan`) and that refusal is the
 * product behaviour this task was asked for. Pre-empting it in the
 * browser would hide the one control that proves the rule is enforced,
 * and a UI that only ever offers legal input cannot demonstrate that
 * illegal input is rejected. The current plan is MARKED in the select
 * instead, so choosing it is informed rather than accidental.
 */
export function planAssignmentBlocker(input: {
  readonly planKey: string;
  readonly reason: string;
  readonly assignable: ReadonlyArray<{ readonly key: string }>;
}): 'no_plan' | 'unknown_plan' | 'no_reason' | null {
  const key = input.planKey.trim();
  if (key.length === 0) return 'no_plan';
  if (!input.assignable.some((plan) => plan.key === key)) return 'unknown_plan';
  if (input.reason.trim().length === 0) return 'no_reason';
  return null;
}

export const PLAN_ASSIGNMENT_BLOCKER_MESSAGES_AR: Readonly<
  Record<Exclude<ReturnType<typeof planAssignmentBlocker>, null>, string>
> = {
  no_plan: 'اختر خطة أولاً.',
  unknown_plan: 'هذه الخطة ليست ضمن الخطط المتاحة للتعيين.',
  no_reason: 'السبب مطلوب لكل تغيير خطة.',
};

/**
 * The banner an operator reads before deciding — Task 307A, the design's
 * contextual banners, restricted to the four this schema can actually
 * support. `over_limit` is NOT one of them: usage aggregation is out of
 * scope, and a limit banner with no limit behind it is a claim the
 * product cannot keep.
 */
export type StandingBanner = {
  readonly tone: 'warn' | 'arch' | 'bad';
  readonly title: string;
  readonly body: string;
};

export function standingBanner(
  state: CommercialState,
  completion: { readonly filled: number; readonly total: number },
): StandingBanner {
  const missing = Math.max(0, completion.total - completion.filled);
  switch (state) {
    case 'pending_activation':
      return missing > 0
        ? {
            tone: 'warn',
            title: 'الملف النظامي غير مكتمل',
            body:
              `ما زال ${missing} من الحقول المطلوبة فارغاً. التفعيل ممكن الآن، ` +
              'لكن الأفضل طلب استكمال الملف أولاً.',
          }
        : {
            tone: 'arch',
            title: 'الملف النظامي مكتمل',
            body: 'كل الحقول المطلوبة مستوفاة. يمكن اتخاذ قرار التفعيل.',
          };
    case 'suspended':
      return {
        tone: 'bad',
        title: 'الوكالة موقوفة',
        body:
          'وصول جميع مستخدمي الوكالة ومستخدمي عملائها إلى المنتج معطّل. ' +
          'البيانات محفوظة ولم يُحذف شيء.',
      };
    case 'terminated':
      return {
        tone: 'arch',
        title: 'سجل مؤرشف',
        body: 'لا توجد انتقالات متاحة. السجل للقراءة فقط ويُحتفظ به للأغراض النظامية.',
      };
    default:
      return {
        tone: 'arch',
        title: 'الوكالة نشطة',
        body: 'الوكالة مفعّلة ويمكن لمستخدميها استخدام المنتج.',
      };
  }
}
